import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { ArrowRight, Play } from 'lucide-react';

export const HeroSection = () => {
  const { t, isRTL } = useLanguage();

  const scrollToForm = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center bg-background pt-16 md:pt-20" data-testid="hero-section">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-brand/5 to-transparent" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-12 md:py-32">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-20 items-center">
          {/* Text Content */}
          <div className={`space-y-6 md:space-y-8 ${isRTL ? 'lg:order-2' : ''}`}>
            <div className="space-y-4 md:space-y-6">
              <h1 
                className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold tracking-tight text-primary leading-tight"
                data-testid="hero-headline"
              >
                {t('hero.headline')}
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl">
                {t('hero.subheadline')}
              </p>
              <p className="text-brand font-medium flex items-center gap-2 text-sm sm:text-base">
                <Play className="w-4 h-4 sm:w-5 sm:h-5 fill-brand" />
                {t('hero.cta')}
              </p>
            </div>

            <div className={`flex flex-col sm:flex-row gap-3 sm:gap-4 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
              <Button
                onClick={scrollToForm}
                data-testid="hero-primary-cta"
                className="h-12 sm:h-14 px-6 sm:px-8 rounded-full bg-brand hover:bg-brand/90 text-white text-base sm:text-lg font-semibold shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-transform duration-300"
              >
                {t('hero.primaryBtn')}
                <ArrowRight className={`w-4 h-4 sm:w-5 sm:h-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
              </Button>
              <Button
                onClick={scrollToForm}
                variant="outline"
                data-testid="hero-secondary-cta"
                className="h-12 sm:h-14 px-6 sm:px-8 rounded-full border-2 border-brand text-brand hover:bg-brand/5 text-base sm:text-lg font-semibold transition-colors duration-300"
              >
                {t('hero.secondaryBtn')}
              </Button>
            </div>
          </div>

          {/* Image */}
          <div className={`relative mt-8 lg:mt-0 ${isRTL ? 'lg:order-1' : ''}`}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.pexels.com/photos/326514/pexels-photo-326514.jpeg"
                alt="Professional website design"
                className="w-full h-auto object-cover aspect-[4/3]"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent" />
            </div>
            {/* Floating Badge */}
            <div className="absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-6 bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl border border-slate-100">
              <div className="text-2xl sm:text-4xl font-bold text-brand">100+</div>
              <div className="text-slate-600 text-xs sm:text-sm">{t('hero.happyBusinesses')}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
